import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type { IAppRegion } from '~/lib/appSettings';
import type {
  IApiRespondInvitationRequestBody,
  IApiUpdateInvitationResponseBody,
} from '../InvitationsApi.types';
import { useApi } from '~/lib/api/useApi';
import { organizationUsersQueryKeyFactory } from '../../Organizations/hooks/useOrganizationUsersQuery';
import { invitationQueryKeyFactory } from './useInvitationQuery';
import { organizationInvitationsQueryKeyFactory } from './useOrganizationInvitationsQuery';

export type IUseDeclineInvitationMutationValues = {
  organizationId: string;
  invitationId: string;
  appRegion: IAppRegion;
  body: IApiRespondInvitationRequestBody;
};

export const useDeclineInvitationMutation = (): UseMutationResult<
  IApiUpdateInvitationResponseBody,
  unknown,
  IUseDeclineInvitationMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: IUseDeclineInvitationMutationValues) =>
      api.invitations.decline(
        values.invitationId,
        values.appRegion,
        values.body,
      ),
    onSuccess: async (_, values) =>
      Promise.all(
        [
          { queryKey: organizationInvitationsQueryKeyFactory(values) },
          { queryKey: invitationQueryKeyFactory(values) },
          { queryKey: organizationUsersQueryKeyFactory(values) },
        ].map((filters) => queryClient.invalidateQueries(filters)),
      ),
  });
};
